/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { message, Spin } from "antd";
import {
  initializeAuthFromCookies,
  getRedirectPath,
  isAuthenticated,
  updateUserinfoWithAccountData,
  getAccountServiceByCookies,
} from "../../utils/authUtils";
import constants from "../../constants/constants";

/**
 * CookieAuth Component
 * Handles cookie-based authentication and auto-login
 * This component checks for authentication cookies and automatically
 * logs in the user if valid cookies are found
 */
const CookieAuth = () => {
  const cookieAuthTriggeredRef = useRef(false);
  const hasCalledAccountServiceRef = useRef(false); // Track if we've already called getAccountService
  const [isChecking, setIsChecking] = useState(true);

  // Check for cookies and auto-login on component mount
  useEffect(() => {
    const checkCookiesAndAutoLogin = async () => {
      try {
        setIsChecking(true);
        
        // Check if already authenticated via localStorage
        if (isAuthenticated()) {
          const userinfo = JSON.parse(localStorage.getItem("userinfo"));
          const userId = localStorage.getItem("userId");

          // If we have userId, redirect directly (skip Login completely)
          if (userId) {
            console.log("CookieAuth: Already authenticated, redirecting");
            const redirectPath = getRedirectPath(userinfo);
            // Prevent redirecting to "/" - redirect to dashboard instead
            const finalPath = redirectPath === "/" ? "/dashboard" : redirectPath;
            // Keep loader visible until redirect completes
            window.location.replace(finalPath);
            return;
          }

          // If we have userinfo but no userId, fetch account service (skip Login)
          // Only call if we haven't already called it
          if (userinfo && userinfo.username && !hasCalledAccountServiceRef.current) {
            console.log("CookieAuth: Found localStorage auth, fetching account service");
            cookieAuthTriggeredRef.current = true;
            hasCalledAccountServiceRef.current = true; // Mark as called
            
            try {
              const accountResult = await getAccountServiceByCookies(userinfo.username);
              
              // Extract data from response: { success, message, data: { userInfo, premiseInfo } }
              const accountData = accountResult?.data || accountResult;
              
              // Set userId and userName in localStorage (store the full data structure)
              localStorage.setItem("userId", JSON.stringify(accountData));
              localStorage.setItem(
                "userName",
                JSON.stringify(accountData?.userInfo?.firstName)
              );

              // Update userinfo with account service data
              updateUserinfoWithAccountData(accountResult);

              // Check for saved redirect URL first
              let url = localStorage.getItem("viewClient");
              if (url !== undefined && url !== null && url !== "/") {
                console.log("CookieAuth: Redirecting to saved URL:", url);
                // Keep loader visible until redirect completes
                window.location.replace(url);
              } else {
                // Check if user has ROLE_MD and redirect to Director Dashboard
                const updatedUserinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userRoles = updatedUserinfo?.roles || [];
                if (userRoles.includes(constants.ROLES.ROLE_MD)) {
                  // Keep loader visible until redirect completes
                  window.location.replace("/director-dashboardNew2");
                } else {
                  // Keep loader visible until redirect completes
                  window.location.replace("/dashboard");
                }
              }
              // Don't set isChecking to false - keep loader visible until redirect
              return; // Exit early to prevent duplicate call
            } catch (error) {
              console.error("Error fetching account service:", error);
              message.error(error?.message || "Failed to fetch user information");
              cookieAuthTriggeredRef.current = false;
              hasCalledAccountServiceRef.current = false;
              // Keep loader visible - will redirect or show error
              if (constants.LOGOUT_REDIRECT_URL) {
                window.location.href = constants.LOGOUT_REDIRECT_URL;
              }
            }
          }
        }

        // Try to initialize from cookies
        // Only if we haven't already called getAccountService
        if (!hasCalledAccountServiceRef.current) {
          const userinfo = initializeAuthFromCookies();
          console.log("CookieAuth: initializeAuthFromCookies result:", userinfo);

          if (userinfo && userinfo.username) {
            console.log("CookieAuth: Cookies found, calling getAccountServiceByCookies - SKIPPING LOGIN");
            // Mark that cookie auth triggered the service call
            cookieAuthTriggeredRef.current = true;
            hasCalledAccountServiceRef.current = true; // Mark as called
            
            try {
              // Fetch account service to get userId and other details using cookies
              const accountResult = await getAccountServiceByCookies(userinfo.username);
              
              // Extract data from response: { success, message, data: { userInfo, premiseInfo } }
              const accountData = accountResult?.data || accountResult;
              
              // Set userId and userName in localStorage (store the full data structure)
              localStorage.setItem("userId", JSON.stringify(accountData));
              localStorage.setItem(
                "userName",
                JSON.stringify(accountData?.userInfo?.firstName)
              );

              // Update userinfo with account service data
              updateUserinfoWithAccountData(accountResult);

              // Check for saved redirect URL first
              let url = localStorage.getItem("viewClient");
              if (url !== undefined && url !== null && url !== "/") {
                console.log("CookieAuth: Redirecting to saved URL:", url);
                // Keep loader visible until redirect completes
                window.location.replace(url);
              } else {
                // Check if user has ROLE_MD and redirect to Director Dashboard
                const updatedUserinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userRoles = updatedUserinfo?.roles || [];
                if (userRoles.includes(constants.ROLES.ROLE_MD)) {
                  // Keep loader visible until redirect completes
                  window.location.replace("/director-dashboardNew2");
                } else {
                  // Keep loader visible until redirect completes
                  window.location.replace("/dashboard");
                }
              }
              // Don't set isChecking to false - keep loader visible until redirect
            } catch (error) {
              console.error("Error fetching account service:", error);
              message.error(error?.message || "Failed to fetch user information");
              cookieAuthTriggeredRef.current = false;
              hasCalledAccountServiceRef.current = false;
              if (constants.LOGOUT_REDIRECT_URL) {
                // Keep loader visible until redirect completes
                window.location.href = constants.LOGOUT_REDIRECT_URL;
              }
              // Keep loader visible - will redirect or stay on page
            }
          } else {
            console.log("CookieAuth: No cookies found, redirecting to logout URL");
            // No cookies found - redirect to logout URL (do not show login page)
            if (constants.LOGOUT_REDIRECT_URL) {
              // Keep loader visible until redirect completes
              window.location.href = constants.LOGOUT_REDIRECT_URL;
            }
            // Keep loader visible - will redirect or stay on page
            // If no logout URL configured, component will return null (blank page)
          }
        } else {
          // Already called getAccountService, keep loader visible while waiting for redirect
          // Don't set isChecking to false - loader will stay visible
        }
      } catch (error) {
        console.error("Error in cookie-based auto-login:", error);
        // On error, redirect to logout URL (do not show login page)
        if (constants.LOGOUT_REDIRECT_URL) {
          // Keep loader visible until redirect completes
          window.location.href = constants.LOGOUT_REDIRECT_URL;
        }
        // Keep loader visible - will redirect or stay on page
      }
    };

    checkCookiesAndAutoLogin();
  }, []);

  // Always show loader immediately - keep it visible until navigation completes
  // This prevents any blank screen during authentication check and redirect
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
      }}
    >
      <Spin size="large" tip="Loading..." />
    </div>
  );
};

export default CookieAuth;

